import { zodResolver } from '@hookform/resolvers/zod'
import { useFetcher } from '@remix-run/react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAuthenticityToken } from 'remix-utils/csrf/react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { ProgressBar } from '#app/components/progressBar.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import {
	type BasicFormState,
	type WizardForm,
	type GetNextQuestionIdAndAnswerParams,
} from '#types/form/wizard.ts'
import { HandleJSONState } from '../utils/handleJSONState.ts'
import { handlePreviousButtonClick } from '../utils/handlePreviousButtonClick.ts'
import { handleRegularState } from '../utils/handleRegularState.ts'
import { FormContent } from './utils/formContent.tsx'
import { FormError } from './utils/formError.tsx'
import { FormHeader } from './utils/formHeader.tsx'
import { FormNavigation } from './utils/formNavigation.tsx'
import { FormSubmitting } from './utils/formSubmitting.tsx'
import { onSubmitCaller } from './utils/onsubmitCaller.ts'
import { useFormValidation } from './utils/useFormValidation.tsx'
import { useWizardFormTransitionAndResetState } from './utils/useWizardFormTransitionAndResetState.tsx'

export function WizardForm<T extends BasicFormState>({
	data,
	getSteps,
	formText,
	currentStepProps,
	translationStrings,
	actionRoute,
	useGlobalFormStore,
}: WizardForm<T>) {
	const fetcher: any = useFetcher()
	const submit = fetcher.submit
	const { t } = useTranslation(translationStrings)
	const CSRFToken = useAuthenticityToken()
	const dispatch = useGlobalFormStore(
		useShallow((state: any) => state.dispatch),
	)
	const entireGlobalState = useGlobalFormStore(
		useShallow((state: any) => state),
	)
	const formState = useGlobalFormStore(
		useShallow((state: any) => state.formState),
	)
	const currentStep = useGlobalFormStore((state: any) => state.currentStep)
	const isInJsonTree = useGlobalFormStore((state: any) => state.isInJsonTree)
	const currentQuestionId = useGlobalFormStore(
		(state: any) => state.currentQuestionId,
	)
	const type = useGlobalFormStore((state: any) => state.type)
	const isBusy = fetcher.state

	const { component, validator, isLastStep, formHasBeenCompleted, fieldKey } =
		currentStepProps

	useEffect(() => {
		if (fetcher.data && fetcher.data?.type === 'reset_state_with_new_data') {
			const newEntireState = fetcher.data?.serverData

			dispatch({
				type: 'SET_ENTIRE_STATE',
				payload: newEntireState,
			})
		}
		if (fetcher.data && fetcher.data?.type === 'set_new_chat_message') {
			const messages = fetcher.data?.messages

			dispatch({
				type: 'SET_MESSAGE',
				messages,
				setMessage: true,
			})
		}
	}, [dispatch, fetcher])

	const {
		register,
		handleSubmit,
		control,
		formState: { isValid },
		trigger,
		getValues,
		setValue,
		watch,
		resetField,
	} = useForm({
		defaultValues: formState as any,
		shouldUnregister: false,
		resolver: zodResolver(formHasBeenCompleted ? z.any() : validator),
	})

	const { isTransitioning, showPrevious } =
		useWizardFormTransitionAndResetState(
			data,
			isInJsonTree,
			currentQuestionId,
			currentStep,
			type,
			dispatch,
		)

	useFormValidation({
		fieldKey,
		data,
		setValue,
		currentQuestionId,
		getValues,
		trigger,
		isValid,
	})

	const onSubmit = async (
		currentFormContext: GetNextQuestionIdAndAnswerParams,
	) => {
		onSubmitCaller<any>({
			type,
			actionRoute: actionRoute as string,
			HandleJSONState,
			resetField,
			submit,
			handleRegularState,
			currentStepProps,
			CSRFToken,
			useGlobalFormStore: entireGlobalState,
			dispatch: dispatch,
			currentFormContext,
			formHasBeenCompleted,
		})
	}
	const onInvalid = (data: any) => {
		console.error('Form errors on invalid submission:', data)
	}

	return (
		<div className="flex h-full flex-col">
			<FormHeader title={formText.title} subtitle={formText.subtitle} />
			<ProgressBar
				currentStep={currentStep as any}
				stepsLength={getSteps().length + 1}
			/>
			<ScrollArea className="h-full">
				<fetcher.Form
					onSubmit={handleSubmit(onSubmit, onInvalid)}
					className="h-full"
					action={actionRoute && actionRoute}
				>
					<fieldset className="flex h-full flex-col justify-between ">
						{formHasBeenCompleted ? (
							<div className="my-5 flex flex-col gap-2 text-[1rem] text-primary-400">
								<h1 className=" font-bold">{formText.finishedText}</h1>
								<p>{formText.finishedSubtitle}</p>
							</div>
						) : (
							<FormContent
								isBusy={isBusy}
								isTransitioning={isTransitioning}
								currentStep={currentStep}
								currentQuestionId={currentQuestionId}
								component={component}
								register={register}
								getValues={getValues}
								dispatch={dispatch}
								control={control}
								handleSubmit={handleSubmit}
								onSubmit={onSubmit}
								onInvalid={onInvalid}
								watch={watch}
								t={t}
							/>
						)}
						<FormError data={data} isBusy={isBusy} isValid={isValid} />
						<FormSubmitting isBusy={isBusy} isTransitioning={isTransitioning} />
					</fieldset>
				</fetcher.Form>
			</ScrollArea>
			<FormNavigation
				fieldKey={fieldKey}
				getValues={getValues}
				onSubmit={onSubmit}
				showPrevious={showPrevious}
				isBusy={isBusy}
				isLastStep={isLastStep}
				handlePreviousClick={() => {
					handlePreviousButtonClick({
						dispatch: dispatch,
						entireGlobalState,
					})
				}}
				isValid={isValid}
				formHasBeenCompleted={formHasBeenCompleted}
				completedFormProps={{
					completedButtonText: formText.finishedButtonText,
				}}
			/>
		</div>
	)
}
